/**
 * ONE STAGE, BUILT ONE WAY, WHEREVER A MATCH IS MOUNTED.
 *
 * Two elements. The FRAME owns the window: exactly one viewport tall, no
 * document scroll, no rubber-banding, and a lit backdrop instead of a black
 * field. The HOST owns the renderer and is a portrait column capped by
 * `MATCH_STAGE_MAX_ASPECT` — a 9:16 board aspect-fitted into a landscape window
 * is a 24 %-wide strip surrounded by nothing, so the renderer is simply never
 * handed a landscape box.
 *
 * `100dvh`, never `100%`: `#app` is a growing flex item, a percentage height
 * there resolves against the content, and the scene's ResizeObserver closes
 * that loop into a page that extends without ever settling.
 *
 * ── THE SAME LAW, NOW THAT THE STAGE ALSO LIVES INSIDE THE META SHELL ───────
 * The match is mounted into the shell's content column (`main.ts`, the
 * `ROUTES.arena` branch), under a header and — below `md` — over a tab bar, so
 * it can no longer be one viewport tall. IA-AND-NAVIGATION §3.6 proposed
 * `height:100%` for the contained case and `_errata/ia-and-nav.md` E1 rejects
 * it, naming the paragraph above: the shell's frame is `min-h-[100dvh]` and its
 * `<main>` is `flex-1` with no `min-h-0`, so a percentage height there has
 * nothing definite to resolve against, and `MatchScene`'s ResizeObserver on the
 * host (`match/scene.ts:593-600`) closes exactly the documented loop.
 *
 * So the contained height is a DEFINITE LENGTH too, and it is the only thing
 * the contained mode changes:
 *
 *     height: var(--rga-stage-h, 100dvh)
 *     --rga-stage-h: calc(100dvh - <measured chrome>px)     ← published by JS
 *
 * The chrome is the shell's HEADER, plus its bottom tab bar below `md`. Neither
 * element's height can depend on the stage's — they size to their own content —
 * so the feedback edge that produced the never-settling page does not exist
 * here: nothing in the formula reads the stage, its frame, or its container.
 * `test/arena-in-shell.test.ts` iterates the recompute and asserts it is a
 * fixed point after one step, which is the property `height:100%` fails.
 *
 * ── THE WIDTH IS DELIBERATELY UNCHANGED ────────────────────────────────────
 * `.rga-stage` in `styles/app.css` still owns it: `width:100%`, and in
 * landscape `min(100%, calc(100dvh * 0.46))`. `100%` is the CONTAINER's width,
 * so the stage can never exceed the content column, and the landscape cap is
 * computed from the VIEWPORT's height rather than the (shorter) stage's — which
 * errs on the side of a WIDER stage, never a narrower one. The board is
 * height-bound at every size (`coords.ts`), so the extra width it can never
 * reach is spent on backdrop, not on a smaller board. `containedStageBox()`
 * below is that arithmetic, and it is what proves the 320x568 and 1920x1080
 * cases are sane. Errata E2's better cap — one computed from `--rga-stage-h`
 * itself — is an `app.css` rule keyed on {@link CONTAINED_STAGE_CLASS}; this
 * module sets no width inline precisely so that rule can land without fighting
 * an inline style it could never beat.
 */
import { MATCH_STAGE_CLASS, MATCH_STAGE_MAX_ASPECT, type StageBox } from './stage-box';

/**
 * The custom property that carries the stage's definite height.
 *
 * UNSET IS THE NORMAL CASE: the full-viewport arena, the onboarding gate's
 * tutorial match and every test resolve the `100dvh` fallback and are byte-for
 * -byte what they were. Only the shell-contained mount publishes it, and only
 * onto the element it owns.
 */
export const STAGE_HEIGHT_VAR = '--rga-stage-h';

/** Both elements' height and max-height. The fallback IS today's behaviour. */
const STAGE_HEIGHT = `var(${STAGE_HEIGHT_VAR}, 100dvh)`;

/**
 * The marker for a stage that is NOT the viewport.
 *
 * Nothing in this module's sizing depends on it — it is the hook `e2e` uses to
 * tell the two stages apart, and the selector errata E2's `app.css` rule is
 * written against. A class that only marks is safe to add before that rule
 * exists; a class that SIZED would be a silent no-op until it did.
 */
export const CONTAINED_STAGE_CLASS = 'rga-stage--contained';

/**
 * Where the stage gets its height from.
 *
 * `'viewport'` is the full-bleed surface (`/arena` before this change, and the
 * onboarding gate for as long as it mounts outside the shell).
 * `'container'` is the shell's content column.
 */
export type StageFill = 'viewport' | 'container';

export interface MatchStage {
  /** Append THIS to the routed root. */
  readonly frame: HTMLElement;
  /** Pass THIS to `startMatch({ host })`. */
  readonly host: HTMLElement;
}

export function createMatchStage(doc: Document, fill: StageFill = 'viewport'): MatchStage {
  const frame = doc.createElement('div');
  frame.className = 'rga-stage-frame';
  frame.style.cssText =
    'position:relative;display:flex;align-items:center;justify-content:center;' +
    `width:100%;height:${STAGE_HEIGHT};max-height:${STAGE_HEIGHT};overflow:hidden;` +
    // `min-height:0` only, and only in the contained case. E1 also called for
    // `flex:1 1 auto`, on the premise that the frame is a flex item in the
    // shell's `<main>`. IT IS NOT: `<main>`'s flex item is `shell.ts`'s
    // screenHost (`<div class="flex-1 min-w-0">`, display:block), and the frame
    // is an ordinary block child of THAT. A flex shorthand here would be inert,
    // so it is not written. `min-height:0` is kept because it costs nothing and
    // stays correct if the frame ever does become a flex item.
    (fill === 'container' ? 'min-height:0;' : '') +
    'background:radial-gradient(120% 90% at 50% 0%, #16121c 0%, #0a0910 60%, #060509 100%);' +
    'touch-action:none;overscroll-behavior:none';

  const host = doc.createElement('div');
  host.className =
    fill === 'container' ? `${MATCH_STAGE_CLASS} ${CONTAINED_STAGE_CLASS}` : MATCH_STAGE_CLASS;
  // The scene measures `host.clientWidth/Height`, so the stage has to be a real
  // box before `startMatch` runs. `100dvh` keeps the mobile URL bar from
  // cropping the hand off the bottom of the board (§6); `--rga-stage-h`, when a
  // shell publishes one, keeps the shell's own chrome from doing the same.
  // WIDTH IS NOT SET HERE. `.rga-stage` in `styles/app.css` owns it, because
  // the portrait cap applies only in landscape and that needs a media query.
  host.style.cssText =
    `position:relative;height:${STAGE_HEIGHT};max-height:${STAGE_HEIGHT};` +
    'overflow:hidden;touch-action:none;overscroll-behavior:none;' +
    'box-shadow:0 0 60px rgba(0,0,0,0.65)';
  frame.appendChild(host);
  return { frame, host };
}

/* ═════════════════════════════════════════════════ the height, as a value ══ */

/**
 * The value {@link STAGE_HEIGHT_VAR} takes for a given chrome height, CSS px.
 *
 * A pure string builder so the one expression that decides the stage's height
 * can be asserted without a layout engine. Zero (and anything that is not a
 * usable number — a detached element measures 0, a mid-teardown read can be
 * `NaN`) collapses to plain `100dvh` rather than to `calc(100dvh - NaNpx)`,
 * which is an INVALID declaration: `var()` substitution would leave the height
 * invalid at computed-value time, the property would fall back to `auto`, and
 * the renderer would be handed a zero-height box — a black screen, not a
 * mis-sized one.
 */
export function stageHeightValue(chromePx: number): string {
  const px = Number.isFinite(chromePx) && chromePx > 0 ? Math.round(chromePx) : 0;
  return px === 0 ? '100dvh' : `calc(100dvh - ${px}px)`;
}

/**
 * The contained stage's box, in CSS px — the arithmetic the CSS performs.
 *
 * MIRRORS, and must keep mirroring, two declarations:
 *   height — `var(--rga-stage-h)` = `calc(100dvh - chrome)`  (this module)
 *   width  — `.rga-stage` in `styles/app.css:147-150`, whose landscape cap is
 *            `min(100%, calc(100dvh * 0.46))`, i.e. the VIEWPORT's height and
 *            the CONTAINER's width.
 *
 * `viewW > viewH` is `@media (orientation: landscape)`, which is a property of
 * the VIEWPORT and not of the container. That is the whole of errata E2: a
 * cap applied unconditionally would compute `0.46 x 434 = 200 px` inside a
 * 320 px portrait phone, and `max-width:100%` cannot widen an element back.
 */
export function containedStageBox(
  viewW: number,
  viewH: number,
  containerW: number,
  chromePx: number,
): StageBox {
  const vw = Number.isFinite(viewW) && viewW > 0 ? viewW : 1;
  const vh = Number.isFinite(viewH) && viewH > 0 ? viewH : 1;
  const cw = Number.isFinite(containerW) && containerW > 0 ? containerW : 1;
  const chrome = Number.isFinite(chromePx) && chromePx > 0 ? chromePx : 0;
  // NOTHING ON THE RIGHT-HAND SIDE IS A CONTAINER MEASUREMENT. That is the
  // no-growth proof, stated as code: `h` cannot move when the container does.
  const h = Math.max(0, vh - chrome);
  const w = vw > vh ? Math.min(cw, vh * MATCH_STAGE_MAX_ASPECT) : cw;
  return { w, h };
}

/* ══════════════════════════════════════════ publishing it from the shell ══ */

/**
 * Tailwind's `md`, which is where the meta shell changes shape.
 *
 * At and above it the tab bar is the LEFT rail (`shell.ts`'s `rail` wrapper,
 * grid column 1) and costs the content column no vertical space; below it the
 * same element is the bottom bar and costs 56 px plus the safe-area inset.
 * Written here as a query because the shell writes it as `md:` class prefixes
 * and the two must agree — IA §3.7 asks for one exported constant, and this is
 * it until `shell.ts` takes over publishing the property (see below).
 */
export const SHELL_MD_QUERY = '(min-width: 48rem)';

/** The shell's own test ids, which are also its only stable handles. */
const SHELL_SELECTOR = '[data-testid="meta-shell"]';
const HEADER_SELECTOR = '[data-testid="meta-header"]';
const TABS_SELECTOR = '[data-testid="meta-tabs"]';

/**
 * Publish {@link STAGE_HEIGHT_VAR} on `host` from the meta shell's chrome, and
 * keep it current. Returns the unbinder; calling it twice is safe.
 *
 * ── WHAT IS MEASURED, AND WHY IT CANNOT LOOP ────────────────────────────────
 * The HEADER's height, and below `md` the TAB BAR's. Both size to their own
 * content — the header wraps to two rows on a narrow phone, which is exactly
 * the change this has to react to — and neither can be moved by the stage. The
 * observers are therefore attached to THOSE ELEMENTS ONLY. Observing the host,
 * the frame or `<main>` is the loop errata E1 names, and it is the reason this
 * function takes the element to write to rather than the element to read.
 *
 * ── WHY IT LIVES HERE AND NOT IN `shell.ts` ─────────────────────────────────
 * E1 says the SHELL should publish this, and it should: the shell owns the
 * header it measures. It is here because the shell is another track's file and
 * this one is not — the code is written so that move is a cut and a paste, with
 * `host` becoming the shell's own `frame`. Nothing else depends on where it
 * runs; the property inherits, so any ancestor of the stage will do.
 *
 * A shell that is not there yet (or a caller outside one) publishes nothing and
 * the stage keeps its `100dvh` fallback — degraded to today's behaviour rather
 * than broken into a zero-height box.
 */
export function trackShellStageHeight(host: HTMLElement): () => void {
  const doc = host.ownerDocument;
  const view = doc.defaultView;
  const shell = host.closest(SHELL_SELECTOR);
  const header = shell?.querySelector<HTMLElement>(HEADER_SELECTOR) ?? null;
  const tabs = shell?.querySelector<HTMLElement>(TABS_SELECTOR) ?? null;
  // `matchMedia` is absent in jsdom, so this is optional-chained rather than
  // assumed — and `null` reads as "not desktop", which counts the tab bar. That
  // is the safe direction: over-counting the chrome makes the stage shorter,
  // under-counting hides the bottom of the card hand behind the tab bar.
  const desktop = view?.matchMedia?.(SHELL_MD_QUERY) ?? null;

  const publish = (): void => {
    const bottom = desktop?.matches === true ? 0 : (tabs?.offsetHeight ?? 0);
    host.style.setProperty(STAGE_HEIGHT_VAR, stageHeightValue((header?.offsetHeight ?? 0) + bottom));
  };
  publish();

  const off: (() => void)[] = [];

  // ResizeObserver where it exists (the header genuinely changes height: it
  // wraps to a second row below ~430 px, `shell.ts`'s `stats` docblock).
  const Observer = (view as (Window & { ResizeObserver?: typeof ResizeObserver }) | null)
    ?.ResizeObserver;
  if (typeof Observer === 'function') {
    const ro = new Observer(publish);
    for (const el of [header, tabs]) if (el !== null) ro.observe(el);
    off.push(() => ro.disconnect());
  }

  // The fallback, and not only a fallback: `100dvh` itself changes when the
  // mobile URL bar collapses, and a browser that reports no size change on the
  // header would otherwise leave the stage sized for the previous viewport.
  if (view !== null) {
    const onResize = (): void => publish();
    view.addEventListener('resize', onResize);
    off.push(() => view.removeEventListener('resize', onResize));
    // Crossing `md` moves the tab bar from under the content to beside it, so
    // the subtraction changes even though nothing resized.
    if (desktop?.addEventListener !== undefined) {
      desktop.addEventListener('change', onResize);
      off.push(() => desktop.removeEventListener('change', onResize));
    }
  }

  return () => {
    for (const release of off) release();
    off.length = 0;
    // Leave the element as it was found. The shell discards the node on
    // teardown anyway, but a stale definite height on a REUSED host would size
    // the next screen to a chrome measurement nobody is maintaining.
    host.style.removeProperty(STAGE_HEIGHT_VAR);
  };
}
